
'use client';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { PlusCircle, UploadCloud, Type, UserPlus, ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { EditableWelcomeImage } from "@/components/editable-welcome-image";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";

const ICONS: { [key: string]: React.ElementType } = {
  PlusCircle,
  UploadCloud,
  Type,
  UserPlus,
};

interface WelcomeStep {
  icon: string;
  title: string;
  description: string;
  imageKey: string;
}

const initialSteps: WelcomeStep[] = [
  {
    icon: "PlusCircle",
    title: "1. Navigate to 'Add Artwork'",
    description: "You can find the 'Add Artwork' button in the main header or on your 'My Collection' page. This is your starting point for cataloging a new piece.",
    imageKey: "step1ImageUrl",
  },
  {
    icon: "UploadCloud",
    title: "2. Upload Your Image",
    description: "Drag and drop your artwork's image into the upload area, or simply click to select a file from your computer. A clear image is key!",
    imageKey: "step2ImageUrl",
  },
  {
    icon: "Type",
    title: "3. Fill in the Details",
    description: "Provide a title for your artwork. Then, select the artist from the dropdown list. If the artist isn't there, just type their name to create a new profile for them instantly.",
    imageKey: "step3ImageUrl",
  },
  {
    icon: "UserPlus",
    title: "4. Save to Your Collection",
    description: "Once you've filled in the details, click the 'Add Artwork' button at the bottom. Your piece will be securely saved to your private collection.",
    imageKey: "step4ImageUrl",
  },
];


export default function WelcomePage() {
    const [steps, setSteps] = useState<WelcomeStep[]>(initialSteps);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const docRef = doc(db, 'site_content', 'welcome_page');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().steps) {
                setSteps(docSnap.data().steps);
            }
            // If it doesn't exist, it will use the `initialSteps` state.
            // An admin saving any image will create the document.
            setLoading(false);
        }, (error) => {
            console.error("Error fetching welcome page content:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);


    return (
        <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-1 bg-muted/40">
            <div className="container mx-auto px-4 py-12 sm:px-6 lg:py-16">
                <header className="mb-12 text-center">
                    <h1 className="font-headline text-4xl sm:text-5xl font-bold text-foreground">
                    Welcome to Art Vault!
                    </h1>
                    <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mt-4">
                    We're excited to have you. Here’s a quick guide to get you started on building your beautiful digital collection.
                    </p>
                </header>

                {loading ? (
                    <div className="flex justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <section className="space-y-12">
                        {steps.map((step, index) => {
                            const Icon = ICONS[step.icon] || PlusCircle;
                            return (
                                <Card key={step.title} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 md:grid md:grid-cols-2 md:items-center">
                                    <div className={`relative aspect-video ${index % 2 === 1 ? 'md:order-last' : ''}`}>
                                        <EditableWelcomeImage imageKey={step.imageKey} title={step.title} />
                                    </div>
                                    <div className="p-6 md:p-8">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="p-3 rounded-full bg-primary/10 text-primary border border-primary/20">
                                                <Icon className="h-6 w-6" />
                                            </div>
                                            <h2 className="font-headline text-2xl text-foreground">{step.title}</h2>
                                        </div>
                                        <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                                    </div>
                                </Card>
                            )
                        })}
                    </section>
                )}


                <section className="mt-16 text-center">
                    <Card className="max-w-2xl mx-auto p-8 bg-background shadow-2xl border-primary border-t-4">
                        <CardHeader>
                            <CardTitle className="font-headline text-3xl">Ready to Begin?</CardTitle>
                            <CardDescription className="text-lg pt-2">
                                You're all set! Time to add your first masterpiece.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button size="lg" asChild className="font-semibold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
                                <Link href="/artworks/new">
                                    Add My First Artwork <ArrowRight className="ml-2 h-5 w-5" />
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                </section>
            </div>
        </main>
        <Footer />
        </div>
    );
}
